import Spacer from 'Pages/Home/Components/Atoms/Spacer';
import { useGetViewType } from 'Pages/Home/Hooks/useGetViewType';
import React, { useMemo } from 'react';
import KickImage from 'Reuseables/KickImage';
import ReactPlaceholder from 'react-placeholder/lib';
import { TextBlock, RectShape } from 'react-placeholder/lib/placeholders';
import ArticleDate from '../../Atoms/ArticleDate';
import ArticleImage from '../../Atoms/ArticleImage';
import ArticleTags from '../../Atoms/ArticleTags';
import ArticleTitle from '../../Atoms/ArticleTitle';
import ArticleBreadCrumb from '../../Atoms/ArticleBreadcrumb';

import { IArticleContentProps } from './ArticleContent.definitions';

/** CSS import */
import Styles from './ArticleContent.module.scss';

const CustomPlaceholder: React.FC = () => {
  const { isDesktopView } = useGetViewType();

  return (
    <div className={Styles['placeholder']}>
      <div className={Styles['placeholder-item']}>
        <TextBlock rows={1} color="#eeeeee" style={{ width: '20%' }} />
        <Spacer height={12} />
        <TextBlock rows={1} color="#eeeeee" style={{ width: '100%' }} />
        <Spacer height={24} />
        <RectShape color="#eeeeee" className={Styles['rect']} />
        <Spacer height={48} />
        <TextBlock
          rows={isDesktopView ? 30 : 20}
          color="#eeeeee"
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );
};

const ArticleContent: React.FC<IArticleContentProps> = (
  props: IArticleContentProps,
) => {
  const { articleData, isReady, isFetching } = props;

  const category = React.useMemo(() => {
    const name = articleData?.categories?.[0]?.name ?? '';

    switch (name) {
      case 'Trending': {
        return 'Sneakers';
      }
      case 'Features': {
        return 'Fashion';
      }
      case 'Local Scene': {
        return 'Luxury';
      }
      default:
        break;
    }

    return name ?? '';
  }, [articleData]);
  const title = React.useMemo(() => {
    return articleData?.title ?? '';
  }, [articleData]);
  const url = React.useMemo(() => {
    let url = '/kickavenews/';
    let slug = articleData?.categories?.[0]?.slug ?? '';

    switch (slug) {
      case 'trending': {
        slug = 'sneakers';
        break;
      }
      case 'features': {
        slug = 'fashion';
        break;
      }
      case 'local-scene': {
        slug = 'luxury';
        break;
      }
      default:
        break;
    }

    return slug ? url + slug : '';
  }, [articleData]);

  const renderEmpty = useMemo(
    () => (
      <div className={Styles['wrapper-empty']}>
        <p className={Styles['title']}>Oh snap!</p>
        <p className={Styles['subtitle']}>
          The articles you're looking for doesn't seem to exists.
        </p>
      </div>
    ),
    [],
  );

  const renderArticle = useMemo(
    () => (
      <>
        <ArticleBreadCrumb category={category} title={title} url={url} />
        <ArticleTitle text={articleData.title} />
        <ArticleDate text={articleData.created_at} />
        <ArticleImage url={articleData.thumbnail?.URL} />
        <div
          className={Styles['content-text']}
          dangerouslySetInnerHTML={{ __html: articleData.body }}
        />
        <div className={Styles['tag-container']}>
          {articleData.tags.map((tag) => {
            return <ArticleTags key={tag.id} text={tag.name} slug={tag.slug} />;
          })}
        </div>
      </>
    ),
    [articleData, category, title, url],
  );

  return (
    <div className={Styles['wrapper']}>
      <ReactPlaceholder
        customPlaceholder={<CustomPlaceholder />}
        ready={!isFetching}
      >
        {articleData.id ? renderArticle : renderEmpty}
      </ReactPlaceholder>
    </div>
  );
};

export default ArticleContent;
